import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { renderRoutes } from 'react-router-config';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { LinearProgress } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import { fetchDisciplines } from 'actions';

import { ImpersonationBanner, NavBar, TopBar } from './components';

export const NAV_BAR_WIDTH = 256;
export const NAV_BAR_WIDTH_COLLAPSED = 72;

const COLLAPSED_STORAGE_KEY = 'navBarCollapsed';

/**
 * Remember the choice across page loads.
 *
 * A secretary collapses the menu to win screen width for a category that is
 * being run right now; every navigation would otherwise throw the width away
 * and make them do it again.
 */
const readStoredCollapsed = () => {
    try {
        return 'true' === window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
    } catch (e) {
        // Private browsing, or a locked-down profile. The menu still
        // collapses; it just forgets, which beats refusing to render.
        return false;
    }
};

const storeCollapsed = collapsed => {
    try {
        window.localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? 'true' : 'false');
    } catch (e) {
        /* see readStoredCollapsed */
    }
};

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  topBar: {
    zIndex: 2,
    position: 'relative'
  },
  container: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden'
  },
  navBar: {
    zIndex: 3,
    width: NAV_BAR_WIDTH,
    minWidth: NAV_BAR_WIDTH,
    flex: '0 0 auto',
    transition: theme.transitions.create(['width', 'min-width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  navBarCollapsed: {
    width: NAV_BAR_WIDTH_COLLAPSED,
    minWidth: NAV_BAR_WIDTH_COLLAPSED
  },
  content: {
    overflowY: 'auto',
    flex: '1 1 auto'
  }
}));

const Dashboard = props => {
  const { route } = props;

  const classes = useStyles();
  const [openNavBarMobile, setOpenNavBarMobile] = useState(false);
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);

  // The federations, once for every page: their badge colours are their own
  // settings (DisciplineBadges, 30 Oct 2026).
  const dispatch = useDispatch();
  const disciplines = useSelector(store => store.disciplines || {});
  useEffect(() => {
    if (!disciplines.isFetched && !disciplines.isFetching) {
      dispatch(fetchDisciplines());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavBarMobileOpen = () => {
    setOpenNavBarMobile(true);
  };

  const handleNavBarMobileClose = () => {
    setOpenNavBarMobile(false);
  };

  const handleToggleCollapsed = useCallback(() => {
    setCollapsed(previous => {
      storeCollapsed(!previous);

      return !previous;
    });
  }, []);

  /**
   * A collapsed rail has nowhere to put a submenu, so an item that has one
   * expands the menu instead of opening into 72px of nothing.
   */
  const handleExpand = useCallback(() => {
    setCollapsed(false);
    storeCollapsed(false);
  }, []);

  return (
    <div className={classes.root}>
      <ImpersonationBanner />
      <TopBar
        className={classes.topBar}
        onOpenNavBarMobile={handleNavBarMobileOpen}
      />
      <div className={classes.container}>
        <NavBar
          className={clsx(classes.navBar, collapsed && classes.navBarCollapsed)}
          collapsed={collapsed}
          onExpand={handleExpand}
          onMobileClose={handleNavBarMobileClose}
          onToggleCollapsed={handleToggleCollapsed}
          openMobile={openNavBarMobile}
        />
        <main className={classes.content}>
          <Suspense fallback={<LinearProgress />}>
            {renderRoutes(route.routes)}
          </Suspense>
        </main>
      </div>
    </div>
  );
};

Dashboard.propTypes = {
  route: PropTypes.object
};

export default Dashboard;
